import { createClient } from '@supabase/supabase-js'
import { PersistenceProvider, QueryBuilder } from './provider.interface.js'
import { config } from '../config.js'

/**
 * Supabase 持久化提供商实现
 */
export class SupabaseProvider extends PersistenceProvider {
  constructor() {
    super()
    
    const url = config.SUPABASE_URL || 'https://your-project-id.supabase.co'
    const anonKey = config.SUPABASE_ANON_KEY || 'your-anon-key'
    
    this.client = createClient(url, anonKey, {
      auth: {
        persistSession: true,
        autoRefreshToken: true,
        detectSessionInUrl: true
      }
    })
  }

  async signUp(email, password, metadata = {}) {
    return this.client.auth.signUp({
      email,
      password,
      options: {
        data: metadata
      }
    })
  }

  async signIn(email, password) {
    return this.client.auth.signInWithPassword({
      email,
      password
    })
  }

  async signOut() {
    return this.client.auth.signOut()
  }

  async getSession() {
    return this.client.auth.getSession()
  }

  async getUser() {
    return this.client.auth.getUser()
  }

  onAuthStateChange(callback) {
    const { data: { subscription } } = this.client.auth.onAuthStateChange(
      (event, session) => callback(event, session?.user || null, session || null)
    )
    return subscription
  }

  async ensureUserProfile(user, username) {
    const { data: existingProfile } = await this.client
      .from('users')
      .select('id')
      .eq('id', user.id)
      .single()

    if (!existingProfile) {
      await this.client
        .from('users')
        .insert({
          id: user.id,
          username: user.user_metadata?.username || username,
          email: user.email
        })
    }
  }

  from(collection) {
    return new SupabaseQueryBuilder(this.client.from(collection))
  }

  getDatabase() {
    return this.client
  }
}

/**
 * Supabase 查询构建器
 */
class SupabaseQueryBuilder extends QueryBuilder {
  constructor(query) {
    super()
    this.query = query
    this.selectedFields = null
    this.pendingFilters = []
    this.pendingOrders = []
  }

  applyPendingFilters() {
    if (!this.query) {
      return
    }

    const canEq = typeof this.query.eq === 'function'
    const canIn = typeof this.query.in === 'function'
    const canOrder = typeof this.query.order === 'function'

    if (this.pendingFilters.length && (canEq || canIn)) {
      const remainingFilters = []
      for (const filter of this.pendingFilters) {
        if (filter.type === 'eq' && canEq) {
          this.query = this.query.eq(filter.column, filter.value)
        } else if (filter.type === 'in' && canIn) {
          this.query = this.query.in(filter.column, filter.value)
        } else {
          remainingFilters.push(filter)
        }
      }
      this.pendingFilters = remainingFilters
    }

    if (this.pendingOrders.length && canOrder) {
      for (const order of this.pendingOrders) {
        this.query = this.query.order(order.column, order.options)
      }
      this.pendingOrders = []
    }
  }

  select(fields = '*') {
    const normalizedFields = typeof fields === 'string' && fields.trim() ? fields.trim() : '*'
    this.selectedFields = normalizedFields
    this.query = this.query.select(normalizedFields)
    this.applyPendingFilters()
    return this
  }

  async insert(data) {
    return this.query.insert(data).select().single()
  }

  update(data) {
    this.query = this.query.update(data)
    this.applyPendingFilters()
    return this
  }

  async updateExec() {
    this.ensureSelect()
    return this.query
  }

  async delete() {
    if (typeof this.query.delete !== 'function') {
      throw new Error('Delete operation is not supported on current query')
    }
    this.query = this.query.delete()
    this.applyPendingFilters()
    return this.query
  }

  eq(column, value) {
    if (typeof this.query.eq === 'function') {
      this.query = this.query.eq(column, value)
    } else {
      this.pendingFilters.push({ type: 'eq', column, value })
    }
    return this
  }

  in(column, values) {
    if (typeof this.query.in === 'function') {
      this.query = this.query.in(column, values)
    } else {
      this.pendingFilters.push({ type: 'in', column, value: values })
    }
    return this
  }

  order(column, options = {}) {
    if (typeof this.query.order === 'function') {
      this.query = this.query.order(column, options)
    } else {
      this.pendingOrders.push({ column, options })
    }
    return this
  }

  ensureSelect() {
    if (typeof this.query.select !== 'function') {
      this.applyPendingFilters()
      return
    }

    const fields = this.selectedFields || '*'
    this.query = this.query.select(fields)
    this.selectedFields = fields
    this.applyPendingFilters()
  }

  async single() {
    this.ensureSelect()
    return this.query.single()
  }

  async exec() {
    this.ensureSelect()
    return this.query
  }
}

export const supabaseConfig = {
  url: config.SUPABASE_URL,
  anonKey: config.SUPABASE_ANON_KEY
}
